import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Share2, Download, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BRAND } from "@/lib/data";

// Inline branded droplet logo (matches /components/Logo.jsx) — exported as a data URL
// so it can be embedded into the QR code center on canvas.
const LOGO_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 80">
  <rect width="64" height="80" rx="14" fill="#FAF7EE"/>
  <path d="M32 14 C 28 22, 24 28, 24 34 C 24 39, 27 42, 32 42 C 37 42, 40 39, 40 34 C 40 28, 36 22, 32 14 Z" fill="#1FB4BC"/>
  <path d="M16 46 C 20 56, 26 60, 32 60 C 38 60, 44 56, 48 46" stroke="#0E5A60" stroke-width="2.4" stroke-linecap="round" fill="none"/>
  <path d="M12 68 Q 20 64, 28 68 T 44 68 T 60 68" stroke="#1FB4BC" stroke-width="2" stroke-linecap="round" fill="none"/>
</svg>
`)}`;

const SHARE_TARGETS = [
  { id: "home", label: "Homepage", path: "/" },
  { id: "book", label: "Book a Massage", path: "/services" },
  { id: "gift", label: "Gift Cards", path: "/gift-cards" },
];

// Build the public URL. Always points to the live custom domain so QR scans
// and shared links never leak the preview host.
function getShareUrl(path) {
  return `https://masonmassage.com${path}`;
}

export default function Share() {
  const [target, setTarget] = useState(SHARE_TARGETS[0]);
  const [copied, setCopied] = useState(false);
  const qrWrapRef = useRef(null);

  const shareUrl = getShareUrl(target.path);

  // Convert the on-screen QR canvas into a high-res branded PNG with a label.
  const buildBrandedPng = () => {
    const sourceCanvas = qrWrapRef.current?.querySelector("canvas");
    if (!sourceCanvas) return null;

    const size = 1024; // export size
    const padding = 80;
    const labelArea = 140;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size + labelArea;
    const ctx = canvas.getContext("2d");

    // Cream background
    ctx.fillStyle = "#FAF7EE";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw the QR code centered
    ctx.drawImage(sourceCanvas, padding, padding, size - padding * 2, size - padding * 2);

    // Brand label underneath
    ctx.fillStyle = "#0E5A60";
    ctx.font = "600 56px Georgia, 'Times New Roman', serif";
    ctx.textAlign = "center";
    ctx.fillText("Mason Massage & Wellness", size / 2, size + 60);

    ctx.fillStyle = "#6b7280";
    ctx.font = "400 32px -apple-system, system-ui, sans-serif";
    ctx.fillText(shareUrl.replace(/^https?:\/\//, ""), size / 2, size + 110);

    return canvas.toDataURL("image/png");
  };

  const handleDownload = () => {
    const dataUrl = buildBrandedPng();
    if (!dataUrl) {
      toast.error("Could not generate QR. Try refreshing the page.");
      return;
    }
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `mason-massage-qr-${target.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("QR code saved!", {
      description: "Check your Downloads (desktop) or Photos (mobile).",
    });
  };

  const handleNativeShare = async () => {
    const shareData = {
      title: "Mason Massage & Wellness",
      text: "Mobile massage in Knoxville — booked to your door.",
      url: shareUrl,
    };

    // Try sharing the image + link if supported (iOS 15+/Android)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        const dataUrl = buildBrandedPng();
        if (dataUrl && navigator.canShare) {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], "mason-massage-qr.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ ...shareData, files: [file] });
            return;
          }
        }
        await navigator.share(shareData);
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
        // Falls through (e.g. blocked in preview iframe)
      }
    }

    // Fallback: copy the link
    handleCopy();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: "linear-gradient(180deg, #0E5A60 0%, #0a4348 100%)" }}
      data-testid="share-page"
    >
      <div className="w-full max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-script text-5xl text-spa-cream mb-2">Share</h1>
          <p className="text-spa-cream/80 text-sm">
            Show, scan, or send — instant access to {BRAND.name}.
          </p>
        </div>

        {/* QR card */}
        <div
          ref={qrWrapRef}
          className="bg-[#FAF7EE] rounded-3xl p-8 shadow-2xl"
          data-testid="qr-card"
        >
          <div className="flex justify-center mb-5">
            <div className="p-3 rounded-2xl bg-white border border-spa-cream/40">
              <QRCodeCanvas
                value={shareUrl}
                size={260}
                bgColor="#FAF7EE"
                fgColor="#0E5A60"
                level="H"
                includeMargin={false}
                imageSettings={{
                  src: LOGO_DATA_URL,
                  height: 56,
                  width: 56,
                  excavate: true,
                }}
                data-testid="qr-canvas"
              />
            </div>
          </div>

          <div className="text-center">
            <p className="font-serif text-xl text-spa-dark mb-1">
              Mason Massage & Wellness
            </p>
            <p className="text-xs text-gray-500 break-all">
              {shareUrl.replace(/^https?:\/\//, "")}
            </p>
          </div>
        </div>

        {/* Target selector */}
        <div className="mt-6">
          <p className="text-spa-cream/70 text-xs uppercase tracking-widest text-center mb-3">
            QR points to
          </p>
          <div className="grid grid-cols-3 gap-2">
            {SHARE_TARGETS.map((t) => {
              const active = t.id === target.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTarget(t)}
                  data-testid={`share-target-${t.id}`}
                  className={`text-xs py-2 px-3 rounded-full border transition-all ${
                    active
                      ? "bg-spa-cream text-spa-dark border-spa-cream font-medium"
                      : "bg-transparent text-spa-cream/80 border-spa-cream/30 hover:border-spa-cream/60"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <Button
            onClick={handleNativeShare}
            data-testid="share-btn"
            className="w-full h-12 bg-[#C9A878] hover:bg-[#b89568] text-spa-dark font-medium rounded-full text-base"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleDownload}
              data-testid="save-qr-btn"
              variant="outline"
              className="h-11 bg-transparent border-spa-cream/40 text-spa-cream hover:bg-spa-cream/10 hover:text-spa-cream rounded-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Save QR
            </Button>
            <Button
              onClick={handleCopy}
              data-testid="copy-link-btn"
              variant="outline"
              className="h-11 bg-transparent border-spa-cream/40 text-spa-cream hover:bg-spa-cream/10 hover:text-spa-cream rounded-full"
            >
              {copied ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        </div>

        {/* Tip */}
        <p className="text-center text-spa-cream/60 text-xs mt-8 leading-relaxed">
          Tip: Save this page to your home screen for one-tap sharing. <br />
          Hold up the QR for clients to scan with their camera.
        </p>
      </div>
    </div>
  );
}
